/**
 * Production surface — the supervisor's "where is every order on the floor" view,
 * in two lenses toggled in-page (the URL `view` search param):
 *   - "station" (default): the station picker with live queue counts; each card
 *     opens a cross-style workbench (`/production/floor/$stage`). Also hosts fabric inward
 *     and the finished-goods board.
 *   - "grid": the order × stage control grid (`ProductionGrid`).
 * Merges the former Floor + Board nav items into one destination.
 */
import { Link } from "@tanstack/react-router";
import { PackageOpen, Factory, LayoutGrid, ChevronRight } from "lucide-react";
import { cn } from "@flint/ui";
import { trpc } from "../../../lib/trpc.js";
import { GARMENT_STATIONS, type GarmentStation } from "../../../lib/garment-stations.js";
import { StrikeApprovalsBanner } from "../../../components/garment/strike-approvals-banner.js";
import { FinishedGoodsBoard } from "./finished-goods-board.js";
import { ProductionGrid } from "./production-grid.js";

export type ProductionView = "station" | "grid";

export function FloorHomePage({ view }: { view: ProductionView }) {
  return (
    <div className="mx-auto w-full max-w-[1200px] p-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight text-ink">Production</h1>
          <p className="mt-1 text-sm text-muted">
            {view === "grid"
              ? "Every in-flight order across every station — pieces cleared per stage."
              : "Every station and its queue — open one to record output, or switch to the grid."}
          </p>
        </div>
        <ViewToggle view={view} />
      </div>

      <StrikeApprovalsBanner className="mt-5" />

      {view === "grid" ? (
        <div className="mt-5">
          <ProductionGrid />
        </div>
      ) : (
        <>
          <InwardCard />

          <h2 className="mb-2.5 mt-8 text-xs font-semibold uppercase tracking-[0.12em] text-muted">
            Stations · the line
          </h2>
          <div className="overflow-hidden rounded-xl border border-line bg-paper shadow-sm">
            {GARMENT_STATIONS.map((s) => (
              <StationRow key={s.key} station={s} />
            ))}
          </div>

          <FinishedGoodsBoard />
        </>
      )}
    </div>
  );
}

function ViewToggle({ view }: { view: ProductionView }) {
  const items: { key: ProductionView; label: string; icon: typeof Factory }[] = [
    { key: "station", label: "By station", icon: Factory },
    { key: "grid", label: "Grid", icon: LayoutGrid },
  ];
  return (
    <div className="inline-flex rounded-lg border border-line bg-raised p-0.5">
      {items.map((it) => {
        const active = view === it.key;
        return (
          <Link
            key={it.key}
            to="/production/floor"
            search={{ view: it.key }}
            className={cn(
              "flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition",
              active ? "bg-paper text-ink shadow-sm" : "text-muted hover:text-ink",
            )}
          >
            <it.icon className="h-4 w-4" /> {it.label}
          </Link>
        );
      })}
    </div>
  );
}

function InwardCard() {
  const q = trpc.materials.inwardBoard.useQuery();
  const t = q.data?.totals;
  const awaiting = t?.awaiting ?? 0;
  const needsInspection = t?.needsInspection ?? 0;
  return (
    <Link
      to="/production/floor/inward"
      className="group mt-5 flex items-center gap-3 rounded-lg border border-line bg-paper p-4 shadow-sm transition hover:border-sky-300"
    >
      <span className="grid h-9 w-9 flex-shrink-0 place-items-center rounded-md bg-sky-50 text-sky-700">
        <PackageOpen size={18} />
      </span>
      <div className="min-w-0 flex-1">
        <div className="text-base font-semibold text-ink">Fabric inward</div>
        <p className="text-xs text-muted">Receive incoming materials against POs, then inspect the fabric.</p>
      </div>
      <div className="flex flex-shrink-0 items-center gap-2 text-xs font-medium">
        {awaiting > 0 ? <span className="rounded-full bg-amber-50 px-2 py-0.5 text-warn">{awaiting} awaiting</span> : null}
        {needsInspection > 0 ? <span className="rounded-full bg-red-50 px-2 py-0.5 text-bad">{needsInspection} to inspect</span> : null}
        {awaiting === 0 && needsInspection === 0 && t ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-ok">clear</span> : null}
      </div>
    </Link>
  );
}

/**
 * One row of the station pipeline: the station and its current workload (pieces
 * pending + orders waiting), or "Clear". Tapping drills into the cross-order
 * queue at that station (`/production/floor/$stage`). Reads `wip.queue`.
 */
function StationRow({ station }: { station: GarmentStation }) {
  const q = trpc.wip.queue.useQuery({ stage: station.key });
  const rows = q.data?.rows ?? [];
  const pendingOrders = rows.filter((r) => r.balance > 0).length;
  const pendingPieces = rows.reduce((sum, r) => sum + Math.max(0, r.balance), 0);
  const loaded = q.data != null && pendingPieces > 0;

  return (
    <Link
      to="/production/floor/$stage"
      params={{ stage: station.key }}
      className="group flex items-center gap-3.5 border-b border-line px-4 py-3.5 transition last:border-0 hover:bg-raised"
    >
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-sky-50 text-sky-700">
        <station.icon size={18} />
      </span>
      <div className="min-w-0 flex-1 text-base font-semibold text-ink">{station.label}</div>

      {q.data == null ? (
        <span className="text-xs text-muted">…</span>
      ) : loaded ? (
        <div className="text-right leading-tight">
          <div>
            <span className="font-mono text-xl font-bold tabular-nums text-ink">
              {pendingPieces.toLocaleString("en-IN")}
            </span>
            <span className="ml-1 text-xs text-muted">pcs</span>
          </div>
          <div className="text-xs text-muted">
            {pendingOrders} order{pendingOrders > 1 ? "s" : ""} waiting
          </div>
        </div>
      ) : (
        <span className="text-sm font-semibold text-ok">Clear</span>
      )}

      <ChevronRight className="h-5 w-5 shrink-0 text-line-2 transition group-hover:text-muted" />
    </Link>
  );
}
